import { customElement, property, state } from 'lit/decorators.js';
import { css, html, LitElement, nothing, PropertyValues } from 'lit';
import '@carbon/web-components/es/components/tabs/tabs.js';
import '@carbon/web-components/es/components/tabs/tab.js';

import Debug from 'debug';
import { EMPTY_STRING } from '../utils';
import { mergeValidationData } from '@rjsf/utils';

const log = Debug('pdq:ui:pdq-search');

@customElement('pdq-tabs')
export class PdqTabs extends LitElement {

  static styles = css`
      :host:not(:defined) {
          display: none;
      }
      :host:not(:defined) ::slotted {
          display: none;
      }
      .tab-content:not([hidden]) {
          display: block;
          width: 100%;
          height: 100%;
      }
      [hidden].tab-content {
          visibility: hidden;
      }
      a:hover {
          color: var(--cds-link-primary-hover, blue);
          background-color: var(--cds-link-primary-hover-background, #f4f4f4);
      }
      a:active {
          color: var(--cds-link-visited, purple)
      }
      .title {
          display: inline-block;
          min-width: fit-content;
      }
      [selected] .title {
          letter-spacing: -0.02em;
      }
  `;


  @state()
  private tabs: PdqTabMeta[] = []

  @property({ type: String }) size = 'md';

  @property({ type: String })
  activeTab = EMPTY_STRING;

  render() {
    return html`
      <cds-tabs style="visibility: hidden" value=${this.activeTab} size=${this.size} @cds-tabs-beingselected=${this.beingSelected}>
        ${this.tabs.map(i => this._renderTab(i))}     
      </cds-tabs>
      <div style="visibility: hidden">
        <slot @slotchange=${this._onSlotChange}></slot>
      </div>
    `
  }

  private _onSlotChange() {
    log(`slot change`)
    this.tabs = this.shadowRoot?.querySelector('slot')?.assignedElements()
    .map((el: any, idx: number) => {
      const id = el.getAttribute('id')

      if (!id)
        console.log(`tag missing id property... tab will not be added and content visibility will remain hidden`, el);

      if (idx == 0 && this.activeTab == EMPTY_STRING)
        this.activeTab = id

      const name = el.getAttribute('tab-name') || `Tab ${idx}`
      const icon = el.getAttribute('tab-icon') || EMPTY_STRING
      const closable = el.getAttribute('tab-closable')

      el.setAttribute('aria-labelledby', `tab-header-${id}`)

      if (this.activeTab != id)
        el.setAttribute('hidden', 'hidden')

      el.classList.add('tab-content')

      log(`slot change: ${id} ${name} ${icon} ${closable}`, el);

      return {id, name, icon, closable}
    }).filter(i => i.id) as PdqTabMeta[] || []

    if (this.activeTab === EMPTY_STRING) {
      this.activeTab = this.tabs[0]?.id || EMPTY_STRING
    }
  }

  private _renderTab(tab: PdqTabMeta) {
    let [icon, closeButton] = [nothing as any, nothing as any]

    if (tab.icon) {
      icon = html`<pdq-icon style="display: flex; margin-right: 4px;" icon="${tab.icon}" size="16"></pdq-icon>`
    }

    if (tab.closable) {
      closeButton = html`<a class="tab-close-button" style="display: flex; margin-left: 4px; z-index: 1001" 
                            @click=${(evt:any) => this.removeTab(evt, tab)}>
        <pdq-icon style="display: flex;" icon="trash" ></pdq-icon>
      </a>`
    }

    return html`<cds-tab 
      target="${tab.id}" 
      value="${tab.id}"
      id="${`tab-header-${tab.id}`}"
      ?selected=${this.activeTab == tab.id} >
      <div style="display: flex; flex-direction: row; justify-content: space-between; align-items: center;">
        ${icon}
        <span class="title">${tab.name}</span>
        ${closeButton}
      </div>
    </cds-tab>`
  }

  private removeTab(evt: CustomEvent, tab: PdqTabMeta) {
    evt.stopImmediatePropagation()
    evt.preventDefault()

    this.shadowRoot?.querySelector(`#tab-header-${tab.id}`)?.remove()
    this.shadowRoot?.querySelector(`slot`)?.assignedElements()
      .filter(i => i.id == tab.id).forEach(i => i.remove())

    if (this.activeTab == tab.id) {
      this.activeTab = this.tabs[0]?.id || EMPTY_STRING
    }
  }

  private beingSelected(tab: CustomEvent) {
    const item = tab.detail.item
    this.shadowRoot?.querySelector('slot')?.assignedElements().forEach((el: any) => {
      if (el.id == item.target) {
        el.removeAttribute('hidden')
      } else {
        el.setAttribute('hidden', 'hidden')
      }
    })
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    this.shadowRoot?.querySelector('cds-tabs')?.setAttribute('style', 'visibility: visible')
    this.shadowRoot?.querySelector('div')?.setAttribute('style', 'visibility: visible')
  }

  addTab(el: HTMLElement) {
    this.shadowRoot?.querySelector('slot')?.appendChild(el)
  }
}

export interface PdqTabMeta {
  id: string
  name: string
  icon: string
  closable: boolean
}
